// frontend/src/services/ticketService.ts
import api from './api';

export interface Ticket {
  id: number;
  title: string;
  description: string;
  customerName: string;
  customerPhone?: string;
  customerId?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_WAY_TO_STORE' | 'ON_WAY_TO_CUSTOMER';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  dueDate?: string;
  startDate?: string;
  endDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  notes?: string;
  attachments?: string[];
  assignedToId?: number;
  createdById: number;
  saleId?: number;
  seamstressName?: string;
  deliveryType?: 'IN_STORE' | 'UBER';
  deliveryAddress?: string;
  modelCode?: string;
  adjustments?: string;
  photos?: string[];
  issueDate?: string;
  cost?: number;
  productId?: string;
  inventoryType?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id_usuario: number;
    username?: string;
    email?: string;
  };
  assignedTo?: {
    id_usuario: number;
    username?: string;
    email?: string;
  };
}

export interface CreateTicketRequest {
  title: string;
  description: string;
  customerName: string;
  customerPhone?: string;
  customerId?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  dueDate?: string;
  saleId?: number;
  seamstressName?: string;
  deliveryType?: 'IN_STORE' | 'UBER';
  deliveryAddress?: string;
  modelCode?: string;
  adjustments?: string;
  photos?: string[];
  issueDate?: string;
  cost?: number;
  productId?: string;
  inventoryType?: string;

  notes?: string;
  assignedToId?: number;
  sellerCode?: string;
  sellerPassword?: string;
}

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  dueDate?: string;
  notes?: string;
  seamstressName?: string;
  deliveryType?: 'IN_STORE' | 'UBER';
  deliveryAddress?: string;
  modelCode?: string;
  adjustments?: string;
  photos?: string[];
  customerPhone?: string;
  cost?: number;
  productId?: string;
  inventoryType?: string;
  assignedToId?: number;
}

export interface AssignTicketRequest {
  assignedToId?: number;
  seamstressName?: string;
  deliveryType?: 'IN_STORE' | 'UBER';
  deliveryAddress?: string;
}

export interface TicketsResponse {
  data: Ticket[];
  total: number;
  limit: number;
  offset: number;
}

export interface TicketResponse {
  ticket: Ticket;
  message?: string;
}

class TicketService {
  // Crear ticket
  async createTicket(data: CreateTicketRequest): Promise<TicketResponse> {
    const response = await api.post('/tickets', data);
    return response.data;
  }

  // Obtener todos los tickets
  async getTickets(params?: {
    status?: string;
    priority?: string;
    assignedToId?: number;
    createdById?: number;
    seamstressName?: string;
    limit?: number;
    offset?: number;
  }): Promise<TicketsResponse> {
    const response = await api.get('/tickets', { params });
    return response.data;
  }

  // Obtener tickets activos
  async getActiveTickets(): Promise<TicketsResponse> {
    const response = await api.get('/tickets/active');
    return response.data;
  }

  // Obtener un ticket por ID
  async getTicketById(id: number): Promise<TicketResponse> {
    const response = await api.get(`/tickets/${id}`);
    return response.data;
  }

  // Actualizar ticket
  async updateTicket(id: number, data: UpdateTicketRequest): Promise<TicketResponse> {
    const response = await api.put(`/tickets/${id}`, data);
    return response.data;
  }

  // Actualizar estado del ticket
  async updateTicketStatus(id: number, status: string): Promise<TicketResponse> {
    const response = await api.patch(`/tickets/${id}/status`, { status });
    return response.data;
  }

  // Asignar ticket
  async assignTicket(id: number, data: AssignTicketRequest): Promise<TicketResponse> {
    const response = await api.patch(`/tickets/${id}/assign`, data);
    return response.data;
  }

  // Eliminar ticket
  async deleteTicket(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/tickets/${id}`);
    return response.data;
  }

  // Notificar al cliente por WhatsApp
  async notifyWhatsApp(id: number, isOnlineStore?: boolean): Promise<{ message: string; whatsappUrl: string; customerPhone: string; customerName: string }> {
    const response = await api.post(`/tickets/${id}/notify-whatsapp${isOnlineStore ? '?isOnlineStore=true' : ''}`);
    return response.data;
  }
}

export const ticketService = new TicketService();
export default ticketService;
